import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ShieldCheck } from 'lucide-react';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import logo from '../../logo_CVUT.jpg';

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h2 className="mt-10 text-xl font-semibold text-ctu-text first:mt-0">{children}</h2>
);

const SubHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 className="mt-6 text-base font-semibold text-ctu-text">{children}</h3>
);

const Para: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="mt-3 text-sm leading-relaxed text-ctu-text-muted">{children}</p>
);

const Bullets: React.FC<{ items: string[] }> = ({ items }) => (
  <ul className="mt-3 list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-ctu-text-muted">
    {items.map((item, i) => (
      <li key={i}>{item}</li>
    ))}
  </ul>
);

/**
 * Public GDPR / privacy notice (single page). Mounted outside AuthGate so it is
 * reachable without signing in — this is what lets the university make the
 * Art. 14 information "publicly available" to researchers who never log in.
 */
export default function PrivacyPage(): React.ReactElement {
  const { t, i18n } = useTranslation('privacy');
  const list = (key: string): string[] => (t(key, { returnObjects: true }) as string[]) ?? [];

  return (
    <div key={i18n.language} className="min-h-screen bg-ctu-bg-page">
      <header className="border-b border-ctu-border bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-6 py-3">
          <Link to="/" className="flex items-center gap-2.5">
            <img src={logo} alt="CTU" className="h-9 w-9 shrink-0 rounded-md object-contain" />
            <span className="text-base font-semibold tracking-tight">Uniconnect</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="flex items-center gap-1.5 text-sm text-ctu-text-muted transition-colors hover:text-ctu-text"
            >
              <ArrowLeft className="h-4 w-4" />
              {t('backToApp')}
            </Link>
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-3xl px-6 py-8">
        <div className="mb-8 flex items-center gap-2.5">
          <ShieldCheck className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-semibold text-ctu-text">{t('pageTitle')}</h1>
        </div>

        <main className="rounded-lg border border-ctu-border bg-white p-6 shadow-card md:p-8">
          {/* Overview */}
          <SectionTitle>{t('overview.title')}</SectionTitle>
          <Para>{t('overview.intro')}</Para>
          <SubHeading>{t('overview.controllerHeading')}</SubHeading>
          <Para>{t('overview.controller')}</Para>

          {/* What data we process */}
          <SectionTitle>{t('data.title')}</SectionTitle>
          <Para>{t('data.intro')}</Para>
          <SubHeading>{t('data.usersHeading')}</SubHeading>
          <Bullets items={list('data.usersData')} />
          <Para>{t('data.usersPurpose')}</Para>
          <Para>{t('data.usersBasis')}</Para>
          <SubHeading>{t('data.researchersHeading')}</SubHeading>
          <Bullets items={list('data.researchersData')} />
          <Para>{t('data.researchersPurpose')}</Para>
          <Para>{t('data.researchersBasis')}</Para>
          <SubHeading>{t('data.sourceHeading')}</SubHeading>
          <Para>{t('data.source')}</Para>

          {/* How expert search works */}
          <SectionTitle>{t('profiling.title')}</SectionTitle>
          <Para>{t('profiling.intro')}</Para>
          <Bullets items={list('profiling.points')} />
          <SubHeading>{t('profiling.noDecisionHeading')}</SubHeading>
          <Para>{t('profiling.noDecision')}</Para>
        </main>
      </div>
    </div>
  );
}
